/* =============================================================
   VALORATIME — MOTION (sistema de animación reutilizable)

   Dos responsabilidades, ambas genéricas (no conocen qué sección
   las usa):

   1. Reveal al entrar en pantalla — busca [data-vt-reveal] y
      [data-vt-reveal-item] en TODA la página, no en una sección
      puntual. Cualquier sección nueva que use estos atributos
      queda cubierta automáticamente, sin tocar este archivo.

   2. Contador — busca [data-vt-counter]; si el texto del elemento
      es un número real (con o sin "%"), lo anima desde 0 al
      entrar en pantalla. Si no lo es ("Free", "Real", cualquier
      texto), no hace nada — se deja el texto tal cual está.

   Se re-escanea el DOM cada vez que el editor de temas inyecta o
   reordena una sección en vivo (evento nativo de Shopify
   "shopify:section:load"), así que agregar/duplicar/reordenar
   secciones desde el editor no rompe nada de esto.
   ============================================================= */
(function () {
  if (window.__vtMotionInit) return;
  window.__vtMotionInit = true;

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Acepta números simples ("14", "100%") y también con coma de miles
  // ("1,465") — un merchant puede escribir cualquiera de las dos formas
  // en el editor y ambas se detectan como número real, sin distinguir
  // a mano cuál formato usó.
  var NUMBER_RE = /^\d{1,3}(?:,\d{3})*(?:\.\d+)?%?$|^\d+(?:\.\d+)?%?$/;

  function parseCounterText(raw) {
    var text = raw.trim();
    if (!NUMBER_RE.test(text)) return null;
    var suffix = text.slice(-1) === '%' ? '%' : '';
    var numStr = (suffix ? text.slice(0, -1) : text).replace(/,/g, '');
    if (!/^\d+(?:\.\d+)?$/.test(numStr)) return null;
    return { target: parseFloat(numStr), suffix: suffix, isInt: numStr.indexOf('.') === -1 };
  }

  /* ---------- Reveal ---------- */
  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    return revealObserver;
  }

  function scanReveal(root) {
    var scope = root || document;
    var obs = getRevealObserver();

    scope.querySelectorAll('[data-vt-reveal]:not([data-vt-scanned])').forEach(function (el) {
      el.setAttribute('data-vt-scanned', '');
      if (prefersReduced) {
        el.classList.add('is-visible');
        return;
      }
      obs.observe(el);
    });

    // Grupos con stagger: cada hijo directo marcado recibe un delay
    // creciente (tope de 8 pasos — de ahí en más entran junto con el
    // último para no alargar la cascada innecesariamente).
    scope.querySelectorAll('[data-vt-reveal-group]:not([data-vt-scanned])').forEach(function (group) {
      group.setAttribute('data-vt-scanned', '');
      var items = group.querySelectorAll(':scope > [data-vt-reveal-item]');
      items.forEach(function (item, i) {
        var step = Math.min(i, 8);
        item.style.setProperty('--vt-delay', String(step * 70));
        if (prefersReduced) {
          item.classList.add('is-visible');
        } else {
          obs.observe(item);
        }
      });
    });
  }

  /* ---------- Contador ---------- */
  function formatNumber(value, isInt) {
    // toLocaleString agrega la coma de miles sola (1465 -> "1,465") —
    // así el número se ve agrupado durante toda la animación, no sólo
    // en el valor final.
    return isInt ? Math.round(value).toLocaleString('en-US') : value.toFixed(1);
  }

  function animateCounter(el, target, suffix, isInt) {
    if (prefersReduced) {
      el.textContent = formatNumber(target, isInt) + suffix;
      return;
    }
    var duration = window.innerWidth < 750 ? 900 : 1400;
    var start = null;

    function step(ts) {
      if (start === null) start = ts;
      var progress = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3); // ease-out cúbico
      var value = target * eased;
      el.textContent = formatNumber(value, isInt) + suffix;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = formatNumber(target, isInt) + suffix;
      }
    }
    requestAnimationFrame(step);
  }

  var counterObserver = null;
  function getCounterObserver() {
    if (counterObserver) return counterObserver;
    counterObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          counterObserver.unobserve(el);
          var parsed = parseCounterText(el.textContent);
          if (!parsed) return; // no es un número real — se deja tal cual
          animateCounter(el, parsed.target, parsed.suffix, parsed.isInt);
        });
      },
      { threshold: 0.4 }
    );
    return counterObserver;
  }

  function scanCounters(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-vt-counter]:not([data-vt-counter-scanned])').forEach(function (el) {
      el.setAttribute('data-vt-counter-scanned', '');
      if (!parseCounterText(el.textContent)) return; // texto no numérico, no aplica
      getCounterObserver().observe(el);
    });
  }

  function scan(root) {
    scanReveal(root);
    scanCounters(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Editor de temas: al agregar, reordenar o duplicar una sección en
  // vivo, Shopify dispara este evento sobre el bloque recién inyectado.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  /* ---------- Microinteracción: bump del ícono del carrito ---------- */
  // "cart:update" ya lo despachan tanto los formularios nativos de
  // Shopify como el de valoratime-home.js al agregar un producto —
  // no hace falta escuchar cada formulario por separado.
  document.addEventListener('cart:update', function () {
    var icon = document.querySelector('cart-icon');
    if (!icon) return;
    icon.classList.remove('vt-bump');
    // forzar reflow para poder re-disparar la animación si se agregan
    // productos varias veces seguidas
    void icon.offsetWidth;
    icon.classList.add('vt-bump');
  });
})();
